import { useCallback, useState } from 'react';
import { Flex, UnstyledButton, Text } from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';

export type SortDir = 'asc' | 'desc';
export type SortState = { field: string; dir: SortDir } | null;

export const useTableSort = (initial: SortState = null) => {
  const [sort, setSort] = useState<SortState>(initial);
  const toggle = useCallback((field: string) => {
    setSort((prev) => {
      if (prev && prev.field === field) {
        return { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { field, dir: 'asc' };
    });
  }, []);
  const reset = useCallback(() => setSort(null), []);
  return { sort, setSort, toggle, reset };
};

export const parseNumericLike = (v: unknown): number => {
  if (v === null || v === undefined) { return NaN; }
  if (typeof v === 'number') { return Number.isFinite(v) ? v : NaN; }
  const s = String(v).trim();
  if (s === '' || s === '-' || s === '—') { return NaN; }
  // Strip currency / percent / "x" suffix / thousands separators, keeping sign + decimal.
  const cleaned = s.replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') { return NaN; }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
};

// Compare two arbitrary cell values. Null/undefined sort last regardless of direction so blank rows never crowd the top of a desc sort.
export const compareSortValues = (a: unknown, b: unknown, dir: SortDir): number => {
  const aMissing = a === null || a === undefined || a === '' || a === '-' || a === '—';
  const bMissing = b === null || b === undefined || b === '' || b === '-' || b === '—';
  if (aMissing && bMissing) { return 0; }
  if (aMissing) { return 1; }
  if (bMissing) { return -1; }
  const na = parseNumericLike(a);
  const nb = parseNumericLike(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) {
    if (na === nb) { return 0; }
    return dir === 'desc' ? (nb - na) : (na - nb);
  }
  const sa = String(a).toLowerCase();
  const sb = String(b).toLowerCase();
  if (sa === sb) { return 0; }
  return dir === 'desc' ? sb.localeCompare(sa) : sa.localeCompare(sb);
};

// Returns a sorted copy of `rows`. Stable for equal keys (preserves original order).
export const sortRows = <T,>(
  rows: T[],
  sort: SortState,
  getValue: (row: T, field: string) => unknown,
): T[] => {
  if (!sort) { return rows; }
  const decorated = rows.map((row, i) => ({ row, i }));
  decorated.sort((a, b) => {
    const cmp = compareSortValues(getValue(a.row, sort.field), getValue(b.row, sort.field), sort.dir);
    if (cmp !== 0) { return cmp; }
    return a.i - b.i;
  });
  return decorated.map((d) => d.row);
};

// Clickable header cell. Shows an arrow only when this column is the active sort.
export const SortableTh = ({
  field,
  sort,
  onToggle,
  children,
  align = 'left',
  thStyle,
  rowSpan,
  colSpan,
  reserveArrow = false,
}: {
  field: string;
  sort: SortState;
  onToggle: (field: string) => void;
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  thStyle?: React.CSSProperties;
  rowSpan?: number;
  colSpan?: number;
  reserveArrow?: boolean;
}) => {
  const active = sort?.field === field;
  // Opt-in: keep the arrow's width on inactive columns, so moving the sort never resizes them.
  const arrow = active
    ? (sort!.dir === 'asc' ? <IconArrowUp size="0.8rem" /> : <IconArrowDown size="0.8rem" />)
    : (reserveArrow ? <IconArrowDown size="0.8rem" style={{ visibility: 'hidden' }} /> : null);
  const justify = align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start';
  return (
    <th style={thStyle} rowSpan={rowSpan} colSpan={colSpan}>
      <UnstyledButton
        onClick={() => onToggle(field)}
        sx={{ width: '100%', textAlign: align }}
      >
        <Flex align="center" gap={4} justify={justify} sx={{ width: '100%' }}>
          <Text size="xs" color="dimmed" transform="uppercase" weight={600} sx={{ whiteSpace: 'nowrap' }}>
            {children}
          </Text>
          {arrow}
        </Flex>
      </UnstyledButton>
    </th>
  );
};
